import { useEffect, useId, useState, type ReactNode } from "react";
import { MIS_3005_D_TABLE_1 } from "@sts/rules-data";
import type { RoomDetailsInput, SurveyRoom } from "../api/types.js";
import { usePortal } from "../app/context.js";
import { useSubmit } from "../app/use-submit.js";
import { roomTypeWords } from "../lib/survey-validation.js";
import { Failure } from "./Async.jsx";
import { adjacencyLabel } from "./SurfacePanel.jsx";

/**
 * The room panel: everything about a room that a person decides, in one place
 * beside the drawing.
 *
 * WHAT IS HERE AND WHY. A scan measures shape. It cannot tell a bedroom from a
 * store, it cannot measure air tightness, and it has no idea whether anybody
 * heats the room. Those three facts set the internal temperature, the
 * ventilation loss and whether the room is in the heat load at all — between
 * them, most of the answer. Until now they could only be changed by a device
 * sync; the designer looking at the plan could see them and not touch them.
 *
 * THE INTERNAL TEMPERATURE IS A FLOOR, NOT A FIGURE. MIS 3005-D:2025
 * §3.4.1(a) requires "internal temperatures not less than those specified in
 * Table 1", so leaving it blank means "the Table 1 minimum for this room
 * type", and the panel says which number that is rather than making the
 * designer look it up. Typing a higher one is a legitimate design decision; a
 * lower one is refused, by this form and by the API, with the reason.
 *
 * THE AIR CHANGE RATE SHOWS THE CALCULATED VALUE AND THE OVERRIDE TOGETHER.
 * The calculated figure here is the scan conversion's, which is the platform's
 * own placeholder and traceable to no standard — so the panel says so, and a
 * designer's figure replaces it as theirs. "Use the calculated figure" puts it
 * back, and is hidden when there is nothing to go back to.
 *
 * MARK AS UNHEATED is the quiet one. A room marked unheated keeps everything
 * it has, leaves the heat load and emitter sizing, and — because the walls
 * between it and the heated rooms are ordinary surfaces with their own
 * adjacency — the heated rooms still lose heat to it. The panel says that in
 * as many words, because "removed from the heat loss" read alone sounds like
 * the neighbours got cheaper too.
 */

const ROOM_TYPES = Object.keys(MIS_3005_D_TABLE_1.data) as (keyof typeof MIS_3005_D_TABLE_1.data)[];

/** A typed temperature, or why it cannot be used. `""` clears it. */
export function parseDesignTemperature(
  raw: string,
  minimumC: number,
): { value: number | null } | { error: string } {
  const trimmed = raw.trim().replace(/\s*°?\s*C?$/i, "").replace("−", "-");
  if (trimmed === "") return { value: null };
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return { error: "The temperature has to be a number, in °C." };
  if (value < minimumC) {
    return {
      error:
        `MIS 3005-D Table 1 requires not less than ${minimumC} °C for this room type. ` +
        `If nobody heats this room, mark it unheated instead.`,
    };
  }
  if (value > 50) return { error: "That is above 50 °C — check the units." };
  return { value };
}

/** A typed air change rate, or why it cannot be used. `""` means "leave it". */
export function parseAirChangeRate(raw: string): { value: number } | { error: string } {
  const trimmed = raw.trim();
  if (trimmed === "") return { error: "Enter an air change rate, or use the calculated figure." };
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return { error: "The air change rate has to be a number." };
  if (value < 0 || value > 20) return { error: "That is outside 0 to 20 air changes an hour." };
  return { value };
}

export function RoomPanel({
  room,
  canEdit,
  onSaved,
  onSelectSurface,
}: {
  readonly room: SurveyRoom;
  /** `survey: update`, on a survey that is still a draft. */
  readonly canEdit: boolean;
  /** Saved: re-read the survey and the heat loss. */
  readonly onSaved: () => void;
  /**
   * Select a surface on the drawing by its scan identifier — how the floor
   * line below opens the floor's own panel rather than duplicating its form.
   */
  readonly onSelectSurface?: (externalRef: string) => void;
}): ReactNode {
  const { client } = usePortal();
  const { submit, busy, error } = useSubmit();
  const nameId = useId();
  const typeId = useId();
  const tempId = useId();
  const achId = useId();
  const reasonId = useId();

  const minimum = room.minimumDesignTempC ?? MIS_3005_D_TABLE_1.data[room.type];
  const [name, setName] = useState(room.name);
  const [type, setType] = useState<SurveyRoom["type"]>(room.type);
  const [temp, setTemp] = useState(room.designTempC === null ? "" : String(room.designTempC));
  const [ach, setAch] = useState(String(room.airChangesPerHour.value));
  const [reason, setReason] = useState(room.unheatedReason ?? "");
  const [touched, setTouched] = useState(false);

  // A different room selected on the plan is a different set of values. The
  // room's id is the reset key: without it the boxes would keep the previous
  // room's typing and a Save would write it onto this one.
  useEffect(() => {
    setName(room.name);
    setType(room.type);
    setTemp(room.designTempC === null ? "" : String(room.designTempC));
    setAch(String(room.airChangesPerHour.value));
    setReason(room.unheatedReason ?? "");
    setTouched(false);
  }, [room.id, room.name, room.type, room.designTempC, room.airChangesPerHour.value, room.unheatedReason]);

  const save = (patch: RoomDetailsInput) =>
    submit(async () => {
      await client.updateRoomDetails(room.id, patch);
      onSaved();
    });

  const floors = room.surfaces.filter((s) => s.kind === "floor");
  const parsedTemp = parseDesignTemperature(temp, minimum ?? -50);
  const parsedAch = parseAirChangeRate(ach);
  const achOverridden = room.airChangesPerHour.provenance === "surveyor-entered";
  const canRevertAch = achOverridden && (room.externalRef ?? null) !== null;

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if ("error" in parsedTemp || "error" in parsedAch) return;
    void save({
      name: name.trim(),
      type,
      designTempC: parsedTemp.value,
      airChangesPerHour: parsedAch.value,
    });
  };

  return (
    <section className="room-panel" data-testid="room-panel" aria-label={`Room details for ${room.name}`}>
      <h4 className="room-panel__title">Room</h4>

      <form className="room-panel__form" aria-label={`Room details for ${room.name}`} onSubmit={onSubmit}>
        <div className="room-panel__field">
          <label htmlFor={nameId}>Name</label>
          <input
            id={nameId}
            value={name}
            disabled={!canEdit || busy}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="room-panel__field">
          <label htmlFor={typeId}>Room type</label>
          <select
            id={typeId}
            value={type}
            disabled={!canEdit || busy}
            onChange={(event) => setType(event.target.value as SurveyRoom["type"])}
          >
            {ROOM_TYPES.map((option) => (
              <option key={option} value={option}>
                {roomTypeWords(option)} ({MIS_3005_D_TABLE_1.data[option]} °C)
              </option>
            ))}
          </select>
          {room.typeAssumed === true ? (
            <p className="hint small fail" data-testid="room-type-needed">
              Room type needed for heat loss calcs — the scan could not tell, so it
              has been treated as a {roomTypeWords(room.type)}.
            </p>
          ) : null}
        </div>

        <div className="room-panel__field">
          <label htmlFor={tempId}>Internal temperature, °C</label>
          <input
            id={tempId}
            inputMode="decimal"
            value={temp}
            placeholder={minimum === undefined ? "" : String(minimum)}
            disabled={!canEdit || busy}
            aria-invalid={touched && "error" in parsedTemp}
            onChange={(event) => setTemp(event.target.value)}
            onBlur={() => setTouched(true)}
          />
          <p className="muted small">
            {room.designTempC === null
              ? `Blank means the MIS 3005-D:2025 Table 1 minimum for a ${roomTypeWords(room.type)}: ${minimum} °C.`
              : `MIS 3005-D:2025 Table 1 requires at least ${minimum} °C for a ${roomTypeWords(room.type)}. Clear the box to use it.`}
          </p>
          {touched && "error" in parsedTemp ? (
            <p className="hint small fail">{parsedTemp.error}</p>
          ) : null}
        </div>

        <div className="room-panel__field">
          <label htmlFor={achId}>Air changes an hour</label>
          <input
            id={achId}
            inputMode="decimal"
            value={ach}
            disabled={!canEdit || busy}
            aria-invalid={touched && "error" in parsedAch}
            onChange={(event) => setAch(event.target.value)}
            onBlur={() => setTouched(true)}
          />
          <p className="muted small" data-testid="ach-source">
            {achOverridden
              ? `Your figure. The calculated one came from the scan conversion.`
              : `Calculated: ${room.airChangesPerHour.value} an hour, from the scan conversion — the ` +
                `platform's placeholder, not a figure from any standard. Type your own to replace it.`}
          </p>
          {canRevertAch ? (
            <button
              type="button"
              className="link small"
              disabled={!canEdit || busy}
              onClick={() => void save({ airChangesPerHour: null })}
            >
              Use the calculated figure
            </button>
          ) : null}
        </div>

        {canEdit ? (
          <button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save room"}
          </button>
        ) : null}
      </form>

      {/* The room's floor, in the room panel because that is where a designer
          looks for it — but edited in the floor's own panel rather than
          duplicated here, so there is one place a floor's type and
          construction can be changed and no chance of two forms disagreeing.
          A room with more than one floor gets a line for each. */}
      {floors.length > 0 ? (
        <div className="room-panel__floors" data-testid="room-panel-floors">
          <h5 className="room-panel__subtitle">Floor</h5>
          <ul className="room-panel__floor-list">
            {floors.map((floor) => (
              <li key={floor.id}>
                {adjacencyLabel(floor.adjacency, floor.kind)} ·{" "}
                {floor.construction ?? "no construction set"} ·{" "}
                {floor.uValue.value} W/m²K
                {onSelectSurface !== undefined && floor.externalRef ? (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="link small"
                      onClick={() => onSelectSurface(floor.externalRef!)}
                    >
                      Edit the floor
                    </button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="room-panel__unheated">
        <label className="room-panel__checkbox">
          <input
            type="checkbox"
            checked={room.unheated === true}
            disabled={!canEdit || busy}
            onChange={(event) =>
              void save(
                event.target.checked
                  ? { unheated: true, unheatedReason: reason.trim() === "" ? null : reason.trim() }
                  : { unheated: false },
              )
            }
          />{" "}
          Mark as unheated
        </label>
        <p className="muted small">
          Takes the room out of the heat loss and out of emitter sizing. The heated
          rooms next to it still lose heat to it through their own walls, at whatever
          temperature you set on those walls.
        </p>
        {room.unheated === true ? (
          <div className="room-panel__field">
            <label htmlFor={reasonId} className="small">
              Why (optional)
            </label>
            <input
              id={reasonId}
              value={reason}
              disabled={!canEdit || busy}
              onChange={(event) => setReason(event.target.value)}
              onBlur={() => {
                const next = reason.trim() === "" ? null : reason.trim();
                if (next !== (room.unheatedReason ?? null)) {
                  void save({ unheated: true, unheatedReason: next });
                }
              }}
            />
          </div>
        ) : null}
      </div>

      {room.detailsSetAt ? (
        <p className="muted small room-panel__byline" data-testid="room-panel-byline">
          Set by {room.detailsSetByName ?? "a colleague"} on{" "}
          {new Date(room.detailsSetAt).toLocaleDateString("en-GB")}. A re-scan will not
          replace this room.
        </p>
      ) : null}

      {error !== null ? <Failure error={error} what="that change" /> : null}
    </section>
  );
}
